// client/src/components/pages/LexiStar/PlanBoard/planBoardLogic.js
//
// Pure logic for the daily plan board (LS-09 §5.2, T6-S05; m3-contracts §7.3
// GET /plan-board): the route aggregate-state language, panel ordering, filter
// options, day stepping and the GeoJSON the map draws.
//
// Colour is state, and only state (LS-09 §1.1): on plan = glowing blue,
// delayed = orange, deviating = magenta (animated), not started = dashed grey.
// Every state also carries a glyph and a word — never colour alone.

import { toApiDateString } from '../../../../utils/dateUtils';
import { TONES, STAGE_LABELS } from '../StatusBoard/statusLanguage';

export const ROUTE_STATES = {
  deviating: {
    label: 'Deviating',
    glyph: '⤴',
    hex: '#d946ef',
    rank: 0,
    pill: 'bg-pink-50 text-pink-800 ring-1 ring-inset ring-pink-500',
  },
  delayed: {
    label: 'Delayed',
    glyph: '⏳',
    hex: '#ea580c',
    rank: 1,
    pill: 'bg-orange-100 text-orange-800',
  },
  on_plan: {
    label: 'On plan',
    glyph: '●',
    hex: '#2563eb',
    rank: 2,
    pill: 'bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-500',
  },
  not_started: {
    label: 'Not started',
    glyph: '○',
    hex: '#94a3b8',
    rank: 3,
    pill: 'bg-surface text-slate-600 border border-dashed border-slate-400',
  },
};

export const routeStateMeta = (state) => ROUTE_STATES[state] || ROUTE_STATES.not_started;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Routes with open deviations or delays first (LS-09 §5.2), then by state, then name. */
export function sortRoutes(routes = []) {
  return [...routes].sort((a, b) => {
    const attentionA = num(a.open_deviations) > 0 || a.state === 'delayed' ? 0 : 1;
    const attentionB = num(b.open_deviations) > 0 || b.state === 'delayed' ? 0 : 1;
    if (attentionA !== attentionB) return attentionA - attentionB;
    const r = routeStateMeta(a.state).rank - routeStateMeta(b.state).rank;
    if (r) return r;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

/** "14 planned · 9 en route · 3 done · 2 not started" */
export function countsText(counts = {}) {
  return [
    `${num(counts.planned)} planned`,
    `${num(counts.en_route)} en route`,
    `${num(counts.done)} done`,
    `${num(counts.not_started)} not started`,
  ].join(' · ');
}

/** Tonnes bar: actual of planned, clamped to 0–1 for the bar (the label keeps the real numbers). */
export function tonnesProgress(tonnes = {}) {
  const planned = num(tonnes.planned);
  const actual = num(tonnes.actual);
  return {
    planned,
    actual,
    ratio: planned > 0 ? Math.max(0, Math.min(1, actual / planned)) : 0,
    over: planned > 0 && actual > planned,
  };
}

/** Header tiles for the ten-second answer: routes, trips by state, tonnes, open deviations. */
export function boardTotals(routes = []) {
  const t = {
    routes: routes.length,
    planned: 0,
    en_route: 0,
    done: 0,
    not_started: 0,
    tonnes_planned: 0,
    tonnes_actual: 0,
    open_deviations: 0,
    attention: 0,
  };
  routes.forEach((r) => {
    t.planned += num(r.counts?.planned);
    t.en_route += num(r.counts?.en_route);
    t.done += num(r.counts?.done);
    t.not_started += num(r.counts?.not_started);
    t.tonnes_planned += num(r.tonnes?.planned);
    t.tonnes_actual += num(r.tonnes?.actual);
    t.open_deviations += num(r.open_deviations);
    if (r.state === 'deviating' || r.state === 'delayed' || num(r.open_deviations) > 0) {
      t.attention += 1;
    }
  });
  return t;
}

// ── Filters ────────────────────────────────────────────────────────────────

const asList = (v) => (Array.isArray(v) ? v : v === null || v === undefined || v === '' ? [] : [v]);

/**
 * Customer / route / material options. The board payload names routes; the
 * customer and material a route carries (when the API includes them as
 * `customer(s)` / `material(s)`, or on its trucks) are collected too. Options
 * already offered stay offered while a filter narrows the payload, so the
 * active choice never disappears from its own dropdown.
 */
export function collectFilterOptions(routes = [], previous = null, trips = []) {
  const customers = new Map(previous?.customers?.map((o) => [o.value, o]) || []);
  const materials = new Map(previous?.materials?.map((o) => [o.value, o]) || []);
  const routeOpts = new Map(previous?.routes?.map((o) => [o.value, o]) || []);
  const addNamed = (map, v) => {
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') {
      const value = String(v.id ?? v.value ?? v.name ?? '');
      if (value) map.set(value, { value, label: String(v.name ?? v.label ?? value) });
    } else {
      map.set(String(v), { value: String(v), label: String(v) });
    }
  };
  // Trips of the day (GET /trips) name the customer on the load; the board does not.
  trips.forEach((t) => {
    const load = t.load || {};
    [load.customer, load.consignee, load.consignor].forEach((c) => addNamed(customers, c));
    addNamed(materials, load.material);
  });
  routes.forEach((r) => {
    if (r.route_id) {
      const label =
        r.code && r.name ? `${r.code} · ${r.name}` : r.name || r.code || String(r.route_id);
      routeOpts.set(String(r.route_id), { value: String(r.route_id), label });
    }
    [...asList(r.customers), ...asList(r.customer)].forEach((c) => addNamed(customers, c));
    [...asList(r.materials), ...asList(r.material)].forEach((m) => addNamed(materials, m));
    [...(r.trucks || []), ...(r.trips || [])].forEach((t) => {
      addNamed(customers, t.customer);
      addNamed(materials, t.material);
    });
  });
  const sorted = (map) => [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  return { customers: sorted(customers), materials: sorted(materials), routes: sorted(routeOpts) };
}

// ── Day stepping (SAST calendar days, as the picker shows them) ────────────

export const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' → local Date at midnight (the pickers work in local days). */
export function parseDay(s) {
  if (!DAY_RE.test(String(s || ''))) return null;
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

export const dayString = (date) => toApiDateString(date);

export function shiftDay(s, delta) {
  const d = parseDay(s) || new Date();
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);
  return dayString(next);
}

export const todayString = () => dayString(new Date());

export function dayLabel(s) {
  const d = parseDay(s);
  if (!d) return s || '';
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Map GeoJSON ────────────────────────────────────────────────────────────

/** Geometry | Feature | {geometry} → a GeoJSON geometry, or null. */
export function geometryOf(x) {
  if (!x || typeof x !== 'object') return null;
  if (x.type === 'Feature') return x.geometry || null;
  if (x.type === 'FeatureCollection') return x.features?.[0]?.geometry || null;
  if (typeof x.type === 'string' && Array.isArray(x.coordinates)) return x;
  if (x.geometry) return geometryOf(x.geometry);
  return null;
}

export function coordsOf(geometry) {
  const g = geometryOf(geometry);
  if (!g) return [];
  switch (g.type) {
    case 'Point':
      return [g.coordinates];
    case 'LineString':
    case 'MultiPoint':
      return g.coordinates;
    case 'Polygon':
    case 'MultiLineString':
      return g.coordinates.flat();
    case 'MultiPolygon':
      return g.coordinates.flat(2);
    default:
      return [];
  }
}

/** The point halfway along a line (by vertex count — good enough for a label anchor). */
export function midpointOf(geometry) {
  const pts = coordsOf(geometry);
  if (!pts.length) return null;
  const p = pts[Math.floor(pts.length / 2)];
  return Array.isArray(p) && p.length >= 2 ? { lon: p[0], lat: p[1] } : null;
}

const fc = (features) => ({ type: 'FeatureCollection', features: features.filter(Boolean) });

/**
 * One line feature per route. `focusId` (hover / selection in the panel)
 * highlights one route and dims the rest (LS-09 §5.2).
 */
export function routeFeatures(routes = [], focusId = null) {
  return fc(
    routes.map((r) => {
      const geometry = geometryOf(r.geometry);
      if (!geometry) return null;
      return {
        type: 'Feature',
        properties: {
          route_id: String(r.route_id),
          name: r.name || '',
          state: ROUTE_STATES[r.state] ? r.state : 'not_started',
          color: routeStateMeta(r.state).hex,
          dim: focusId !== null && String(r.route_id) !== String(focusId),
          focus: focusId !== null && String(r.route_id) === String(focusId),
        },
        geometry,
      };
    })
  );
}

/** Advisory geometries (hatched on the map) with their route and a ⚠ anchor. */
export function advisoryItems(routes = []) {
  const items = [];
  routes.forEach((r) => {
    (r.advisories || []).forEach((a, i) => {
      // An advisory names its route, not a segment of it: without its own geometry
      // the whole route line is hatched.
      const geometry = geometryOf(a.geometry) || geometryOf(a.segment) || geometryOf(r.geometry);
      const anchor = midpointOf(geometry) || midpointOf(r.geometry);
      items.push({
        key: `${r.route_id}:${a.id ?? i}`,
        route_id: r.route_id,
        route_name: r.name,
        title: a.title || a.reason || a.reason_code || a.kind || 'Advisory',
        geometry,
        anchor,
        valid_to: a.valid_to || a.expires_at || null,
      });
    });
  });
  return items;
}

export function advisoryFeatures(items = []) {
  return fc(
    items.map((a) =>
      a.geometry
        ? {
            type: 'Feature',
            properties: { key: a.key, kind: a.geometry.type },
            geometry: a.geometry,
          }
        : null
    )
  );
}

/** Truck marker tone from its trip stage (LS-09 §4 business-state palette). */
const STAGE_TONE = {
  DISPATCHED: 'parked',
  ACKNOWLEDGED: 'parked',
  EN_ROUTE_TO_SOURCE: 'empty',
  AT_SOURCE: 'loading',
  IN_TRANSIT_LOADED: 'loaded',
  AT_DESTINATION: 'offloading',
  IN_TRANSIT_EMPTY: 'empty',
  EXCEPTION_HOLD: 'delay',
  COMPLETED: 'parked',
};

export function truckTone(stage) {
  return TONES[STAGE_TONE[stage] || 'parked'];
}

export const registrationOf = (assetKey) => String(assetKey || '').replace(/^(reg|asset):/, '');
export const truckRegistration = (t) => t.registration || registrationOf(t.asset_key);

export function truckTitle(t) {
  const pct = Number.isFinite(Number(t.progress_pct))
    ? ` · ${Math.round(Number(t.progress_pct))}%`
    : '';
  const late = t.delayed ? ' · delayed' : '';
  return `${truckRegistration(t)} · ${STAGE_LABELS[t.stage] || t.stage || 'unknown'}${pct}${late}`;
}

/** Trucks with a position, flattened with their route. */
export function truckItems(routes = []) {
  const out = [];
  routes.forEach((r) =>
    (r.trucks || []).forEach((t) => {
      if (t.lat === null || t.lat === undefined || t.lon === null || t.lon === undefined) return;
      out.push({ ...t, route_id: r.route_id, route_name: r.name });
    })
  );
  return out;
}

/**
 * Site marker: role glyph, the count on the badge (trips out of a source, into
 * a destination, both for a two-way site) and the detail for the tooltip.
 * counts: { outbound, inbound, at_site, planned_tonnes_out, planned_tonnes_in }
 * (a bare number is taken as the total).
 */
export function siteMeta(site) {
  const role = String(site.role || '').toLowerCase();
  const isSource =
    role.includes('source') || role === 'origin' || role === 'from' || role === 'both';
  const isDest = role.includes('dest') || role === 'to' || role === 'both';
  const glyph = isSource && !isDest ? '⬒' : isDest && !isSource ? '⬓' : '⬒⬓';
  const color =
    isSource && !isDest ? TONES.loading.hex : isDest && !isSource ? '#d97706' : '#475569';
  const roleLabel =
    isSource && !isDest ? 'Source' : isDest && !isSource ? 'Destination' : 'Source and destination';
  const c = site.counts;
  if (typeof c === 'number') return { glyph, color, total: c, detail: '', roleLabel };
  const n = (k) => (c && Number.isFinite(Number(c[k])) ? Number(c[k]) : 0);
  const out = n('outbound');
  const inn = n('inbound');
  const at = n('at_site');
  let total;
  if (c && ('outbound' in c || 'inbound' in c)) {
    total = isSource && !isDest ? out : isDest && !isSource ? inn : out + inn;
  } else {
    total = Object.entries(c || {})
      .filter(([k, v]) => !/tonnes/.test(k) && Number.isFinite(Number(v)))
      .reduce((sum, [, v]) => sum + Number(v), 0);
  }
  const parts = [];
  if (out) parts.push(`${out} trip${out === 1 ? '' : 's'} out`);
  if (inn) parts.push(`${inn} in`);
  if (at) parts.push(`${at} at the site now`);
  if (n('planned_tonnes_out')) parts.push(`${Math.round(n('planned_tonnes_out'))} t planned out`);
  if (n('planned_tonnes_in')) parts.push(`${Math.round(n('planned_tonnes_in'))} t planned in`);
  return { glyph, color, total, detail: parts.join(' · '), roleLabel };
}

/** Every [lng, lat] the board draws — for fitting the map (wallboard auto-fit). */
export function boardPoints(routes = [], sites = []) {
  return [
    ...routes.flatMap((r) => coordsOf(r.geometry)),
    ...sites.filter((s) => s.lon != null && s.lat != null).map((s) => [s.lon, s.lat]),
  ];
}
